import { useEffect, useState } from "react";
import { Music2, ExternalLink, Youtube, Instagram } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/brand";
import { Section, PageHeader, Eyebrow } from "@/components/common";

const PROJECT_TYPES = ["Tous", "Single", "EP", "Album", "Clip", "Audiovisuel", "Culturel", "Autre"];

function spotifyArtistId(v) {
  if (!v) return null;
  const m = String(v).match(/artist\/([A-Za-z0-9]+)/);
  if (m) return m[1];
  if (/^[A-Za-z0-9]{18,}$/.test(String(v).trim())) return String(v).trim();
  return null;
}

export default function ArtistesProjets() {
  const [artists, setArtists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("Tous");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "Tous" ? projects : projects.filter((p) => p.type === filter);

  return (
    <div data-testid="artistes-page">
      <PageHeader eyebrow="Artistes & Projets" title="Le catalogue" subtitle="Découvrez les artistes accompagnés par AL Music Records et leurs projets." image={IMG.stageLights} />

      <Section className="py-20">
        <Eyebrow>Artistes</Eyebrow>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl mb-10">Le roster</h2>
        {artists.length === 0 ? (
          <div className="card-dark p-10 text-center text-slate-500 text-sm"><span className="font-mono text-xs uppercase tracking-widest text-amber-300">À compléter</span><p className="mt-3">Les artistes seront ajoutés depuis l'administration.</p></div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {artists.map((a) => (
              <button key={a.id} data-testid={`artist-${a.id}`} onClick={() => setSelected(a)} className="card-dark overflow-hidden group text-left">
                <div className="aspect-[3/4] overflow-hidden bg-[#1A1A20]">
                  {a.photo ? <img src={a.photo} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> : <div className="w-full h-full flex items-center justify-center text-slate-700"><Music2 size={40} /></div>}
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold">{a.name}</h3>
                  {a.genre && <p className="text-xs text-slate-500 mt-1">{a.genre}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section className="pb-24">
        <Eyebrow>Projets</Eyebrow>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl mb-8">Discographie & sorties</h2>
        <div className="flex flex-wrap gap-2 mb-8">
          {PROJECT_TYPES.map((t) => (
            <button key={t} data-testid={`project-filter-${t}`} onClick={() => setFilter(t)} className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition ${filter === t ? "bg-gold text-black border-transparent" : "border-white/15 text-slate-300 hover:border-amber-400/50"}`}>{t}</button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="card-dark p-10 text-center text-slate-500 text-sm"><span className="font-mono text-xs uppercase tracking-widest text-amber-300">À compléter</span></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <div key={p.id} className="card-dark overflow-hidden">
                <div className="aspect-square overflow-hidden bg-[#1A1A20]">
                  {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover" />}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <span className="eyebrow">{p.type}</span>
                    {p.status && <span className="text-[10px] text-slate-500 uppercase">{p.status}</span>}
                  </div>
                  <h3 className="font-display font-bold text-lg mt-2">{p.title}</h3>
                  {p.artist && <p className="text-sm text-slate-500">{p.artist}</p>}
                  {p.description && <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 no-print" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />
          <div className="relative card-dark max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex gap-6">
              {selected.photo && <img src={selected.photo} alt={selected.name} className="w-32 h-40 object-cover rounded-xl" />}
              <div>
                <h3 className="font-display font-black text-2xl">{selected.name}</h3>
                {selected.genre && <p className="text-gold text-sm mt-1">{selected.genre}</p>}
                <div className="flex gap-3 mt-4">
                  {selected.socials?.instagram && <a href={selected.socials.instagram} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-gold"><Instagram size={18} /></a>}
                  {selected.socials?.youtube && <a href={selected.socials.youtube} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-gold"><Youtube size={18} /></a>}
                </div>
              </div>
            </div>
            {selected.bio && <p className="text-slate-300 mt-6 leading-relaxed whitespace-pre-line">{selected.bio}</p>}
            {spotifyArtistId(selected.spotify_id || selected.socials?.spotify) && (
              <div className="mt-6">
                <div className="eyebrow mb-2">Discographie — singles & EP (Spotify)</div>
                <iframe
                  title={`Spotify ${selected.name}`}
                  data-testid="artist-spotify-embed"
                  style={{ borderRadius: 12 }}
                  src={`https://open.spotify.com/embed/artist/${spotifyArtistId(selected.spotify_id || selected.socials?.spotify)}?utm_source=generator`}
                  width="100%"
                  height="352"
                  frameBorder="0"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                />
              </div>
            )}
            {selected.music_links?.length > 0 && (
              <div className="mt-6 space-y-2">
                {selected.music_links.map((l, i) => (
                  <a key={i} href={l.url || l} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-gold hover:underline"><ExternalLink size={14} /> {l.label || l.url || l}</a>
                ))}
              </div>
            )}
            <button onClick={() => setSelected(null)} className="btn-ghost mt-8">Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
